import React, { useState, useMemo } from "react";
import useFetch from "../../hooks/useFetch";
import { useMutation } from "../../hooks/useMutation";
import { useToasts } from "react-toast-notifications";

function SmsTemplate() {
  const { data, isLoading, hasError, errorMessage, refetch: refetchTemplates } = useFetch('admin/sms-template');
  const templates = data?.templates || [];

  // Group templates by category
  const categorizedTemplates = useMemo(() => {
    const grouped = {
      ADMIN: [],
      WORKER: [],
      COMPANY: []
    };

    templates.forEach(template => {
      if (grouped[template.category]) {
        grouped[template.category].push(template);
      }
    });

    // Sort WORKER templates alphabetically by title
    grouped.WORKER.sort((a, b) => a.title.localeCompare(b.title));

    return grouped;
  }, [templates]);

  return (
    <div className="max-w-7xl mx-auto p-4">
      {isLoading && <div>Loading templates...</div>}
      {hasError && <div>Error loading templates: {errorMessage}</div>}
      
      {!isLoading && !hasError && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Column for ADMIN templates */}
          <div>
            <h2 className="text-2xl font-bold mb-4">ADMIN</h2>
            <div className="space-y-6">
              {categorizedTemplates.ADMIN.map((template, index) => (
                <CustomTextArea 
                  key={template.id} 
                  template={template} 
                  index={index}
                  refetch={refetchTemplates}
                  category="ADMIN"
                />
              ))}
            </div>
          </div>

          {/* Column for WORKER templates */}
          <div>
            <h2 className="text-2xl font-bold mb-4">WORKER</h2>
            <div className="space-y-6">
              {categorizedTemplates.WORKER.map((template, index) => (
                <CustomTextArea 
                  key={template.id} 
                  template={template} 
                  index={index}
                  refetch={refetchTemplates}
                  category="WORKER"
                />
              ))}
            </div>
          </div>

          {/* Column for COMPANY templates */}
          <div>
            <h2 className="text-2xl font-bold mb-4">COMPANY</h2>
            <div className="space-y-6">
              {categorizedTemplates.COMPANY.map((template, index) => (
                <CustomTextArea 
                  key={template.id} 
                  template={template} 
                  index={index}
                  refetch={refetchTemplates}
                  category="COMPANY"
                />
              ))}
            </div>
          </div>
        </div>
      )}
      
      {templates?.length === 0 && !isLoading && !hasError && <div>No templates found.</div>}
    </div>
  );
}

export default SmsTemplate;

const CustomTextArea = ({ template, index, category, refetch }) => {
  const [textAreaContent, setTextAreaContent] = useState(template?.template_content || '');
  const {
    isLoading: updatingTemplate,
    mutate: updateTemplate,
  } = useMutation('admin/sms-template', { method: 'PATCH' });

  const { addToast } = useToasts();
  
  const handleTextAreaChange = (e) => {
    setTextAreaContent(e.target.value);
  };
  
  const handleUpdateTemplate = async (id) => {
    try {
      await updateTemplate({ id, template_content: textAreaContent }, id);
      refetch();
      addToast('Template updated successfully!', { 
        appearance: 'success', 
        autoDismiss: true 
      });
    } catch (error) {
      addToast('Something went wrong', { 
        appearance: 'error', 
        autoDismiss: true 
      });
    }
  };

  // Generate prefix based on category
  const getPrefix = () => {
    if (category === 'ADMIN') return `${index + 1}. `;
    if (category === 'WORKER') return `${String.fromCharCode(65 + index)}. `; // A, B, C, etc.
    if (category === 'COMPANY') return `${index + 1}. `;
    return '';
  };

  return (
    <div>
      <label htmlFor={`message-${template?.id}`} className="block mb-2 text-sm font-medium text-gray-900">
        {getPrefix()}<span className="font-bold">{template?.title_in_danish}</span>
      </label>
      <textarea
        id={`message-${template?.id}`}
        rows="7"
        onChange={handleTextAreaChange}
        value={textAreaContent}
        className="block p-2 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:outline-none focus:border-black"
        placeholder="Write your thoughts here..."
        disabled={updatingTemplate}
      />
      <button 
        disabled={updatingTemplate || textAreaContent === template?.template_content} 
        onClick={() => handleUpdateTemplate(template?.id)} 
        type="button" 
        className={`text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg mt-2 text-sm px-5 py-2 me-2 mb-2 focus:outline-none ${
          (updatingTemplate || textAreaContent === template?.template_content) 
            ? 'opacity-50 cursor-not-allowed' 
            : ''
        }`}
      >
        {updatingTemplate ? 'Updating...' : 'Update'}
      </button>
    </div>
  );
};